import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";
import { useEffect, useState } from "react";
import { captureException, captureMessage } from "~/config/sentry";

type NetworkStatusListener = (isOnline: boolean) => void;

const getBrowserOnlineStatus = (): boolean =>
  typeof navigator === "undefined" ? true : navigator.onLine;

// On native, wait for Capacitor's authoritative initial status before allowing
// remote work. On the web, navigator.onLine is available synchronously.
export let currentNetworkOnlineStatus = Capacitor.isNativePlatform()
  ? false
  : getBrowserOnlineStatus();

const listeners = new Set<NetworkStatusListener>();
let initializationPromise: Promise<void> | null = null;
let browserListenersInstalled = false;

const updateNetworkStatus = (isOnline: boolean) => {
  if (currentNetworkOnlineStatus === isOnline) return;
  currentNetworkOnlineStatus = isOnline;
  listeners.forEach((listener) => listener(isOnline));
};

const subscribeNetworkStatus = (
  listener: NetworkStatusListener
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const installBrowserListeners = () => {
  if (browserListenersInstalled || typeof window === "undefined") return;
  browserListenersInstalled = true;
  window.addEventListener("online", () => updateNetworkStatus(true));
  window.addEventListener("offline", () => updateNetworkStatus(false));
};

const asyncIsNetworkOnline = async (): Promise<boolean> => {
  if (!Capacitor.isPluginAvailable("Network")) {
    return getBrowserOnlineStatus();
  }
  const status = await Network.getStatus();
  return status.connected;
};

/** Starts one application-wide connectivity monitor. */
export const initNetworkStatus = (): Promise<void> => {
  if (initializationPromise) return initializationPromise;

  initializationPromise = (async () => {
    if (Capacitor.isPluginAvailable("Network")) {
      let receivedNetworkEvent = false;
      await Network.addListener("networkStatusChange", (status) => {
        receivedNetworkEvent = true;
        updateNetworkStatus(status.connected);
      });
      const initialStatus = await asyncIsNetworkOnline();
      // Native events are newer than the initial snapshot, which may resolve late.
      if (!receivedNetworkEvent) updateNetworkStatus(initialStatus);
      return;
    }

    captureMessage(
      "Network plugin unavailable, using browser's online/offline events"
    );
    installBrowserListeners();
    updateNetworkStatus(getBrowserOnlineStatus());
  })().catch((error) => {
    captureException(error);
    installBrowserListeners();
    updateNetworkStatus(getBrowserOnlineStatus());
  });

  return initializationPromise;
};

/**
 * Reactive view of the shared monitor. Consumers do not install native
 * listeners of their own.
 */
export { subscribeNetworkStatus };

export const useNetwork = () => {
  const [isOffline, setIsOffline] = useState(!currentNetworkOnlineStatus);

  useEffect(() => {
    const listener: NetworkStatusListener = (isOnline) =>
      setIsOffline(!isOnline);
    const unsubscribe = subscribeNetworkStatus(listener);
    // Synchronize after subscribing so a status change cannot be missed.
    listener(currentNetworkOnlineStatus);
    void initNetworkStatus();

    return unsubscribe;
  }, []);

  return { isOffline };
};
